import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Underline } from '@tiptap/extension-underline';
import { Link } from '@tiptap/extension-link';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { FontFamily } from '@tiptap/extension-font-family';
import {
  Bold, Italic, Underline as UnderlineIcon, Heading1, Heading2,
  List, ListOrdered, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Indent, Outdent, Link as LinkIcon, Type, Quote, Undo, Redo, Strikethrough
} from 'lucide-react';

const FONT_CHOICES = [
  { label: 'Serif (default)', value: '' },
  { label: 'Georgia', value: 'Georgia, "Times New Roman", serif' },
  { label: 'Times New Roman', value: '"Times New Roman", Times, serif' },
  { label: 'Helvetica', value: 'Helvetica, Arial, sans-serif' },
  { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
  { label: 'Courier', value: '"Courier New", Courier, monospace' },
];

const ToolbarButton = ({ onClick, active, disabled, title, testId, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    data-testid={testId}
    className={`p-1.5 rounded transition-colors text-starlight/70 hover:text-starlight hover:bg-white/10 ${
      active ? 'bg-etheric/20 text-etheric' : ''
    } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
  >
    {children}
  </button>
);

const Divider = () => <div className="w-px h-5 bg-white/10 mx-1" />;

export default function RichTextEditor({ value, onChange, placeholder = 'Begin writing...' }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false, autolink: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      FontFamily.configure({ types: ['textStyle'] }),
    ],
    content: value || '',
    onUpdate: ({ editor: ed }) => {
      onChange?.(ed.getHTML());
    },
    editorProps: {
      attributes: {
        class:
          'prose-invert prose-sm max-w-none min-h-[280px] focus:outline-none px-4 py-3 text-starlight/90 leading-relaxed',
        'data-placeholder': placeholder,
        'data-testid': 'broadcast-editor-content',
      },
    },
  });

  // Sync external value changes (e.g. when loading a draft) into the editor
  React.useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || '') !== current) {
      editor.commands.setContent(value || '', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes('link').href || '';
    const url = window.prompt('Link URL (leave empty to remove):', prev);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const setFont = (fontValue) => {
    if (!fontValue) {
      editor.chain().focus().unsetFontFamily().run();
    } else {
      editor.chain().focus().setFontFamily(fontValue).run();
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-black/30 overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-white/10 bg-white/[0.03] flex-wrap" data-testid="broadcast-editor-toolbar">
        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo" testId="editor-undo">
          <Undo className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo" testId="editor-redo">
          <Redo className="w-4 h-4" />
        </ToolbarButton>
        <Divider />
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold (Cmd+B)" testId="editor-bold">
          <Bold className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic (Cmd+I)" testId="editor-italic">
          <Italic className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline (Cmd+U)" testId="editor-underline">
          <UnderlineIcon className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough" testId="editor-strike">
          <Strikethrough className="w-4 h-4" />
        </ToolbarButton>
        <Divider />
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading" testId="editor-h2">
          <Heading1 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Subheading" testId="editor-h3">
          <Heading2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Quote" testId="editor-quote">
          <Quote className="w-4 h-4" />
        </ToolbarButton>
        <Divider />
        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bulleted list" testId="editor-ul">
          <List className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered list" testId="editor-ol">
          <ListOrdered className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().sinkListItem('listItem').run()}
          disabled={!editor.can().sinkListItem('listItem')}
          title="Indent list item (Tab)"
          testId="editor-indent"
        >
          <Indent className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().liftListItem('listItem').run()}
          disabled={!editor.can().liftListItem('listItem')}
          title="Outdent list item (Shift+Tab)"
          testId="editor-outdent"
        >
          <Outdent className="w-4 h-4" />
        </ToolbarButton>
        <Divider />
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align left" testId="editor-align-left">
          <AlignLeft className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Align center" testId="editor-align-center">
          <AlignCenter className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align right" testId="editor-align-right">
          <AlignRight className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('justify').run()} active={editor.isActive({ textAlign: 'justify' })} title="Justify" testId="editor-align-justify">
          <AlignJustify className="w-4 h-4" />
        </ToolbarButton>
        <Divider />
        <ToolbarButton onClick={setLink} active={editor.isActive('link')} title="Insert / edit link" testId="editor-link">
          <LinkIcon className="w-4 h-4" />
        </ToolbarButton>
        <div className="flex items-center gap-1 ml-1">
          <Type className="w-4 h-4 text-starlight/40" />
          <select
            onChange={(e) => setFont(e.target.value)}
            value={editor.getAttributes('textStyle').fontFamily || ''}
            className="bg-white/5 border border-white/10 rounded text-xs text-starlight/80 px-1.5 py-1 focus:outline-none"
            title="Font family"
            data-testid="editor-font-select"
          >
            {FONT_CHOICES.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Editor body */}
      <EditorContent editor={editor} />

      {/* Inline styles for the prose look since dark theme + tiptap default */}
      <style>{`
        [data-testid="broadcast-editor-content"] p { margin: 0 0 0.75em 0; }
        [data-testid="broadcast-editor-content"] h2 { font-size: 1.4em; font-weight: 600; margin: 1em 0 0.5em 0; color: #e5e7eb; }
        [data-testid="broadcast-editor-content"] h3 { font-size: 1.15em; font-weight: 600; margin: 0.9em 0 0.4em 0; color: #e5e7eb; }
        [data-testid="broadcast-editor-content"] ul { list-style: disc; padding-left: 1.5em; margin: 0.5em 0; }
        [data-testid="broadcast-editor-content"] ol { list-style: decimal; padding-left: 1.5em; margin: 0.5em 0; }
        [data-testid="broadcast-editor-content"] li { margin: 0.2em 0; }
        [data-testid="broadcast-editor-content"] blockquote { border-left: 3px solid rgba(99,102,241,0.5); padding-left: 1em; color: #cbd5e1; font-style: italic; margin: 0.75em 0; }
        [data-testid="broadcast-editor-content"] a { color: #818cf8; text-decoration: underline; }
        [data-testid="broadcast-editor-content"] strong { color: #f1f5f9; }
        [data-testid="broadcast-editor-content"] em { color: #e2e8f0; }
        [data-testid="broadcast-editor-content"] .ProseMirror-focused { outline: none; }
        [data-testid="broadcast-editor-content"]:focus { outline: none; }
        [data-testid="broadcast-editor-content"] p.is-editor-empty:first-child::before {
          color: rgba(226,232,240,0.3);
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
      `}</style>
    </div>
  );
}
